import React from 'react'
import {
  DropDownButtonComponent,
  ItemModel,
} from '@syncfusion/ej2-react-splitbuttons'
import { BeforeItemRenderEventArgs } from '@syncfusion/ej2-react-dropdowns'
import Button from '@components/common/Button/Button.component'
import './ActionButtons.css'

interface ActionButtonsProps {
  onView?: () => void
  onEdit?: () => void
  onDelete?: () => void
  onDuplicate?: () => void
  onPin?: () => void
  onSave?: () => void
  onPhone?: () => void
  onMail?: () => void
  onMessage?: () => void
  showView?: boolean
  showEdit?: boolean
  showDelete?: boolean
  showMoreOptions?: boolean
  isEditing?: boolean
  customMiddleButton?: React.ReactNode
  disableEdit?: boolean
  disabledEditTooltip?: string
  disableDelete?: boolean
  disabledDeleteTooltip?: string
  isPinned?: boolean
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  onView,
  onEdit,
  onDelete,
  onDuplicate,
  onPin,
  onSave,
  onPhone,
  onMail,
  onMessage,
  showView = true,
  showEdit = true,
  showDelete = true,
  showMoreOptions = true,
  isEditing = false,
  customMiddleButton,
  disableEdit = false,
  disabledEditTooltip = 'Editing is disabled',
  disableDelete = false,
  disabledDeleteTooltip = 'Deletion is disabled',
  isPinned = false,
}) => {
  return (
    <div className="action-buttons">
      {isEditing ? (
        <>
          {onSave && (
            <Button className="act-button act-button-save" onClick={onSave}>
              <i className="mgc mgc_check_fill" />
            </Button>
          )}
          {onDelete && (
            <Button className="act-button act-button-delete" onClick={onDelete}>
              <i className="mgc mgc_delete_2_line" />
            </Button>
          )}
        </>
      ) : (
        <>
          {showView && onView && (
            <Button className="act-button act-button-view" onClick={onView}>
              <i className="mgc mgc_eye_2_fill" />
            </Button>
          )}

          {customMiddleButton}

          {showEdit && onEdit && (
            <Button
              className={`act-button act-button-edit ${
                disableEdit ? 'act-button-edit-disabled' : ''
              }`}
              onClick={onEdit}
              disabled={disableEdit}
              title={disableEdit ? disabledEditTooltip : 'Edit'}
            >
              <i className="mgc mgc_pencil_fill" />
            </Button>
          )}

          {showDelete && onDelete && (
            <Button
              className={`act-button act-button-delete ${
                disableDelete ? 'act-button-delete-disabled' : ''
              }`}
              onClick={onDelete}
              disabled={disableDelete}
              title={disableDelete ? disabledDeleteTooltip : 'Delete'}
            >
              <i className="mgc mgc_delete_2_fill" />
            </Button>
          )}
        </>
      )}

      {showMoreOptions &&
        (onPhone ||
          onMail ||
          onMessage ||
          (onEdit && !showEdit) ||
          (onDelete && !showDelete) ||
          onDuplicate ||
          onPin) && (
          <DropDownButtonComponent
            createPopupOnClick={true}
            items={[
              ...(onEdit && !showEdit
                ? [
                    {
                      text: 'Edit',
                      iconCss: 'mgc mgc_pencil_fill',
                      id: 'edit',
                    },
                  ]
                : []),
              ...(onDuplicate
                ? [{ text: 'Duplicate', iconCss: 'mgc mgc_copy_fill' }]
                : []),
              ...(onPin
                ? [
                    {
                      text: isPinned ? 'Unpin' : 'Pin',
                      iconCss: 'mgc mgc_pin_2_fill',
                    },
                  ]
                : []),
              ...(onDelete && !showDelete
                ? [
                    {
                      text: 'Delete',
                      iconCss: 'mgc mgc_delete_2_fill',
                      id: 'delete',
                    },
                  ]
                : []),
              ...(onPhone
                ? [{ text: 'Phone', iconCss: 'mgc mgc_phone_fill' }]
                : []),
              ...(onMail
                ? [{ text: 'Mail', iconCss: 'mgc mgc_mail_fill' }]
                : []),
              ...(onMessage
                ? [{ text: 'Message', iconCss: 'mgc mgc_chat_2_fill' }]
                : []),
            ]}
            cssClass="custom-dropdown-icon e-caret-hide act-button"
            iconCss="mgc mgc_more_2_fill"
            beforeItemRender={(args: BeforeItemRenderEventArgs) => {
              if (args.item.id === 'edit' && disableEdit) {
                args.element.classList.add('e-disabled')
                args.element.setAttribute('title', disabledEditTooltip)
              }
              if (args.item.id === 'delete' && disableDelete) {
                args.element.classList.add('e-disabled')
                args.element.setAttribute('title', disabledDeleteTooltip)
              }
            }}
            select={(args: { item: ItemModel }) => {
              if (args.item.text === 'Edit' && onEdit && !disableEdit) onEdit()
              else if (args.item.text === 'Duplicate' && onDuplicate)
                onDuplicate()
              else if (
                (args.item.text === 'Pin' || args.item.text === 'Unpin') &&
                onPin
              )
                onPin()
              else if (
                args.item.text === 'Delete' &&
                onDelete &&
                !disableDelete
              )
                onDelete()
              else if (args.item.text === 'Phone' && onPhone) onPhone()
              else if (args.item.text === 'Mail' && onMail) onMail()
              else if (args.item.text === 'Message' && onMessage) onMessage()
            }}
            style={{ height: '24px', width: '24px' }}
          />
        )}
    </div>
  )
}

export default ActionButtons
